import * as React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CampoProps {
  /** Precisa bater com o `id` do controle para que o clique no rótulo funcione. */
  id: string;
  rotulo: string;
  obrigatorio?: boolean;
  erro?: string;
  ajuda?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Envolve um controle de formulário com rótulo, texto de ajuda e mensagem de
 * erro, cuidando das ligações de acessibilidade.
 *
 * O erro é anunciado por leitor de tela (`role="alert"`) e apontado por
 * `aria-describedby` — a cor vermelha sozinha não comunicaria nada a quem não
 * a enxerga.
 */
export function Campo({ id, rotulo, obrigatorio, erro, ajuda, className, children }: CampoProps) {
  const idErro = `${id}-erro`;
  const idAjuda = `${id}-ajuda`;

  const descritores = [erro ? idErro : null, ajuda ? idAjuda : null].filter(Boolean).join(' ');

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium">
        {rotulo}
        {obrigatorio && (
          <>
            <span aria-hidden="true" className="text-destrutivo ml-0.5">
              *
            </span>
            <span className="apenas-leitor-tela"> (obrigatório)</span>
          </>
        )}
      </label>

      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            id,
            'aria-invalid': erro ? true : undefined,
            'aria-describedby': descritores || undefined,
            'aria-required': obrigatorio || undefined,
          })
        : children}

      {ajuda && !erro && (
        <p id={idAjuda} className="text-silenciado-texto text-xs">
          {ajuda}
        </p>
      )}

      {erro && (
        <p id={idErro} role="alert" className="text-destrutivo flex items-center gap-1 text-xs">
          <AlertCircle className="size-3.5 shrink-0" aria-hidden="true" />
          {erro}
        </p>
      )}
    </div>
  );
}
